'use client';

/**
 * /insights — the Data Master Plan, live inside Beast.
 * Everything on this page is computed fresh from seo.db / funnels.db /
 * ads.db / improve.db by /api/insights on every load — no frozen snapshots.
 * Click any site row for the drill-down (trend, band mix, its query queues).
 */
import { useState, useEffect, Fragment } from 'react';
import { BarChart3, Loader2, ChevronDown, TrendingUp, TrendingDown, Minus, CircleAlert, CircleCheck, Hourglass, ExternalLink } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';

const CYAN = '#22d3ee', AMBER = '#f59e0b', GREEN = '#4ade80', RED = '#f87171', GRAY = '#6b7280';
const TIP_STYLE = { backgroundColor: '#0d1117', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 };
const fmtK = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : String(n));

const STATUS: Record<string, { label: string; cls: string }> = {
  now: { label: '✓ converting', cls: 'bg-green-500/15 text-green-300' },
  trajectory: { label: 'on trajectory', cls: 'bg-cyan-500/15 text-cyan-300' },
  stalled: { label: '✕ stalled', cls: 'bg-red-500/15 text-red-300' },
  young: { label: 'too young', cls: 'bg-white/10 text-gray-400' },
};
const VERDICT_CLS: Record<string, string> = {
  improved: 'bg-green-500/15 text-green-300', flat: 'bg-white/10 text-gray-300',
  worse: 'bg-red-500/15 text-red-300', inconclusive: 'bg-amber-500/15 text-amber-300', pending: 'bg-white/10 text-gray-400',
};

function Chip({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="glass-card px-4 py-3 min-w-[130px]">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
      <span className="text-xl font-bold text-white">{value}</span>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Card({ title, sub, children }: { title: string; sub?: string; children: any }) {
  return (
    <div className="glass-card p-5 mb-5">
      <h2 className="text-sm font-semibold text-white">{title}</h2>
      {sub && <p className="text-xs text-gray-500 mt-0.5 mb-3">{sub}</p>}
      {children}
    </div>
  );
}

function QueryTable({ rows, showCtr }: { rows: any[]; showCtr?: boolean }) {
  if (!rows?.length) return <p className="text-xs text-gray-600 py-2">none</p>;
  const max = rows[0]?.im || 1;
  return (
    <table className="w-full text-xs">
      <thead><tr className="text-gray-500">
        <th className="text-left font-medium py-1 pr-2">Site</th>
        <th className="text-left font-medium py-1 pr-2">Query</th>
        <th className="text-right font-medium py-1 pr-2">Impr 28d</th>
        <th className="text-right font-medium py-1 pr-2">Pos</th>
        {showCtr && <th className="text-right font-medium py-1 pr-2">CTR</th>}
        <th className="w-24"></th>
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-white/[0.05] text-gray-300">
            <td className="py-1.5 pr-2 text-gray-500 whitespace-nowrap">{r.domain}</td>
            <td className="py-1.5 pr-2">{r.query}</td>
            <td className="py-1.5 pr-2 text-right tabular-nums">{r.im.toLocaleString()}</td>
            <td className="py-1.5 pr-2 text-right tabular-nums">{r.wp}</td>
            {showCtr && <td className="py-1.5 pr-2 text-right tabular-nums">{r.ctr}%</td>}
            <td className="py-1.5"><div className="h-1.5 rounded-r bg-cyan-400/70" style={{ width: `${Math.max(4, (r.im / max) * 90)}px` }} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SiteDrill({ domain }: { domain: string }) {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    fetch(`/api/insights?view=site&domain=${encodeURIComponent(domain)}`)
      .then(r => r.json()).then(x => (x.error ? setErr(x.error) : setD(x))).catch(e => setErr(String(e)));
  }, [domain]);
  if (err) return <p className="text-xs text-red-300 py-3">{err}</p>;
  if (!d) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;
  const m = d.model;
  const mixTotal = m ? Object.values(m.mix as Record<string, any>).reduce((a: number, v: any) => a + v.im, 0) : 0;
  const bandCols: Record<string, string> = { '1-3': '#0e7490', '4-10': CYAN, '11-20': '#67e8f9', '21-50': '#374151', '50+': '#1f2937' };
  return (
    <div className="grid gap-4 lg:grid-cols-2 py-4">
      <div>
        <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-2">Weighted position — 12 weeks (lower is better)</p>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={m?.weekly || []}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="w" tick={{ fontSize: 10, fill: GRAY }} tickFormatter={(w: string) => w.slice(5)} />
              <YAxis reversed tick={{ fontSize: 10, fill: GRAY }} width={28} domain={['auto', 'auto']} />
              <Tooltip contentStyle={TIP_STYLE} formatter={(v: any, n: any) => [n === 'wp' ? `pos ${v}` : v, n === 'wp' ? 'weighted pos' : 'impressions']} />
              <ReferenceLine y={15} stroke={GREEN} strokeDasharray="4 4" label={{ value: 'clicks begin ~15', fontSize: 10, fill: GREEN, position: 'insideBottomRight' }} />
              <Line type="monotone" dataKey="wp" stroke={CYAN} strokeWidth={2} dot={{ r: 2.5, fill: CYAN }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-gray-500 uppercase tracking-wide mt-4 mb-1.5">Impression mix by position band (28d)</p>
        <div className="flex h-4 rounded overflow-hidden gap-[2px]">
          {['1-3', '4-10', '11-20', '21-50', '50+'].map(b => {
            const im = m?.mix?.[b]?.im || 0;
            return im ? <div key={b} title={`${b}: ${im.toLocaleString()}`} style={{ width: `${(im / mixTotal) * 100}%`, background: bandCols[b] }} /> : null;
          })}
        </div>
        <div className="flex gap-3 mt-1.5 flex-wrap">
          {['1-3', '4-10', '11-20', '21-50', '50+'].map(b => (
            <span key={b} className="text-[10px] text-gray-500 flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ background: bandCols[b] }} />{b}: {fmtK(m?.mix?.[b]?.im || 0)}
            </span>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Queries just outside top-10 (the cliff)</p>
          <QueryTable rows={(d.cliff || []).slice(0, 8)} />
        </div>
        <div>
          <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Ranking but not clicked (CTR laggards)</p>
          <QueryTable rows={(d.laggards || []).slice(0, 6)} showCtr />
        </div>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string>('');
  // Column sort for the click-arrival table. null = server order (impr desc).
  // Clicking a header: first click sorts by that column (numbers default to
  // desc — biggest first is what you look for), second click flips it.
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  useEffect(() => {
    fetch('/api/insights')
      .then(r => r.json()).then(d => (d.error ? setErr(d.error) : setData(d))).catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="p-8"><p className="text-sm text-red-300">Insights failed: {err}</p></div>;
  if (!data) return <div className="flex items-center justify-center h-80"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;

  const monthly = (data.monthly || []).slice(-8);
  const lastM = monthly[monthly.length - 1] || {};
  const sitesRaw: any[] = data.sites || [];
  // Sort accessors. ETA sorts by "when do clicks arrive": converting-now first,
  // then dated ETAs ascending, then young (first data), stalled last — a plain
  // string sort on the label would interleave 'now' with dates meaninglessly.
  const SORT_COLS: Record<string, { get: (s: any) => any; num: boolean }> = {
    site: { get: s => s.domain, num: false },
    status: { get: s => s.status, num: false },
    impr: { get: s => s.impr28 ?? 0, num: true },
    clicks: { get: s => s.clicks28 ?? 0, num: true },
    pos: { get: s => s.wpos28 ?? 999, num: true },
    trend: { get: s => s.slope ?? 0, num: true },
    top10: { get: s => s.top10Share ?? 0, num: true },
    eta: {
      get: s => s.status === 'now' ? '0000'
        : s.etaDate ? `1-${s.etaDate}`
        : s.status === 'young' ? `2-${s.firstDate || ''}` : '3-stalled',
      num: false,
    },
  };
  const sites = sort
    ? [...sitesRaw].sort((a, b) => {
        const c = SORT_COLS[sort.key];
        const av = c.get(a), bv = c.get(b);
        return (c.num ? (av as number) - (bv as number)
                      : String(av).localeCompare(String(bv))) * sort.dir;
      })
    : sitesRaw;
  const clickSort = (key: string) => setSort(prev =>
    prev?.key === key
      ? { key, dir: prev.dir === 1 ? -1 : 1 }
      // numeric columns open with biggest-first; text columns A→Z
      : { key, dir: SORT_COLS[key].num ? -1 : 1 });
  const arrow = (key: string) => sort?.key === key ? (sort.dir === 1 ? ' ▴' : ' ▾') : '';
  const converting = sites.filter(s => s.status === 'now');
  const stalled = sites.filter(s => s.status === 'stalled');
  const improved = (data.verdicts || []).filter((v: any) => v.verdict === 'improved').length;
  const chain = data.chain;
  const cpb = chain?.ads?.spend && chain?.ads?.booked ? (chain.ads.spend / chain.ads.booked).toFixed(2) : null;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
          <BarChart3 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Insights</h1>
          <p className="text-xs text-gray-500">
            Live from seo.db · funnels.db · ads.db · improve.db — computed just now · GSC through {data.anchor}
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="flex gap-3 flex-wrap mb-6">
        <Chip label="Impressions / mo" value={fmtK(lastM.impressions || 0)} sub={`clicks ${(lastM.clicks || 0).toLocaleString()}`} />
        <Chip label="Sites converting" value={String(converting.length)} sub={converting.map(s => s.domain.split('.')[0]).slice(0, 3).join(' · ')} />
        <Chip label="Stalled sites" value={String(stalled.length)} sub="need content/link work" />
        <Chip label="Cliff queries" value={String(data.queues?.cliff ?? '—')} sub="pos 11–20, ready to push" />
        {cpb && <Chip label="Cost / booking" value={`$${cpb}`} sub={chain.project} />}
        <Chip label="Loop verdicts" value={`${improved} ▲`} sub="changes proven improved" />
      </div>

      {/* Monthly divergence */}
      <Card title="Impressions are compounding — clicks are position-gated"
        sub="Monthly totals across all GSC-synced sites. Clicks stay flat until impressions migrate into the top 10.">
        <div className="grid md:grid-cols-2 gap-4">
          {(['impressions', 'clicks'] as const).map(key => (
            <div key={key} className="h-44">
              <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">{key}</p>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthly}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 10, fill: GRAY }} tickFormatter={(m: string) => m.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: GRAY }} width={40} tickFormatter={(v: number) => fmtK(v)} />
                  <Tooltip contentStyle={TIP_STYLE} />
                  <Area type="monotone" dataKey={key} stroke={key === 'impressions' ? CYAN : AMBER} strokeWidth={2}
                    fill={key === 'impressions' ? 'rgba(34,211,238,0.12)' : 'rgba(245,158,11,0.10)'} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </Card>

      {/* CTR bands */}
      <Card title="Your CTR by position band (120 days, every query)"
        sub="Clicks only exist above position ~10 — and even the top bands are 3–10× below potential (title/snippet work).">
        <div className="space-y-2 max-w-2xl">
          {(data.bands || []).map((b: any) => {
            const maxCtr = Math.max(...data.bands.map((x: any) => x.ctr || 0.001));
            return (
              <div key={b.band} className="flex items-center gap-3">
                <span className="w-12 text-right text-xs text-gray-400 tabular-nums">{b.band}</span>
                <div className="flex-1 h-5 relative">
                  <div className="absolute inset-y-0 left-0 rounded-r bg-cyan-400/80" style={{ width: `${Math.max(0.6, (b.ctr / maxCtr) * 100)}%` }} />
                </div>
                <span className="text-xs text-gray-300 tabular-nums w-40">{b.ctr}% · {fmtK(b.im)} impr</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Click-arrival table + drill-down */}
      <Card title="When each site starts earning clicks"
        sub="12-week position trend → ETA to pos ≈ 15, where clicks historically begin. Click a row for the drill-down.">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-500">
            {([['site', 'Site', 'left', ''], ['status', 'Status', 'left', ''],
               ['impr', 'Impr 28d', 'right', 'pr-3'], ['clicks', 'Clicks', 'right', 'pr-3'],
               ['pos', 'Pos', 'right', 'pr-3'], ['trend', 'Trend /wk', 'right', 'pr-3'],
               ['top10', 'Top-10 %', 'right', 'pr-3'], ['eta', 'ETA', 'left', '']] as const
            ).map(([key, label, align, pad]) => (
              <th key={key} onClick={() => clickSort(key)}
                className={`text-${align} font-medium py-1.5 ${pad} cursor-pointer select-none hover:text-gray-300 ${
                  sort?.key === key ? 'text-cyan-300' : ''}`}>
                {label}{arrow(key)}
              </th>
            ))}
            <th className="w-4"></th>
          </tr></thead>
          <tbody>
            {sites.map((s: any) => {
              const st = STATUS[s.status] || STATUS.young;
              const TrendIcon = s.slope == null ? Minus : s.slope < -0.1 ? TrendingUp : s.slope > 0.1 ? TrendingDown : Minus;
              const trendCls = s.slope == null ? 'text-gray-600' : s.slope < -0.1 ? 'text-green-300' : s.slope > 0.1 ? 'text-red-300' : 'text-gray-400';
              return (
                <Fragment key={s.domain}>
                  <tr onClick={() => setOpen(open === s.domain ? '' : s.domain)}
                    className="border-t border-white/[0.05] text-gray-300 hover:bg-white/[0.03] cursor-pointer">
                    <td className="py-2 font-medium text-white">{s.domain}</td>
                    <td className="py-2"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${st.cls}`}>{st.label}</span></td>
                    <td className="py-2 pr-3 text-right tabular-nums">{s.impr28.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{s.clicks28}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{s.wpos28}</td>
                    <td className={`py-2 pr-3 text-right tabular-nums ${trendCls}`}><TrendIcon className="w-3 h-3 inline mr-1" />{s.slope ?? '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{s.top10Share}%</td>
                    <td className="py-2 text-gray-400">{s.status === 'now' ? 'now' : s.etaDate ? `~${s.etaDate}` : s.status === 'young' ? `${s.firstDate} first data` : 'needs intervention'}</td>
                    <td className="py-2"><ChevronDown className={`w-3.5 h-3.5 text-gray-600 transition-transform ${open === s.domain ? 'rotate-180' : ''}`} /></td>
                  </tr>
                  {open === s.domain && (
                    <tr className="border-t border-cyan-500/10 bg-cyan-500/[0.03]">
                      <td colSpan={9}><SiteDrill domain={s.domain} /></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Money chain + verdicts */}
      <div className="grid lg:grid-cols-2 gap-5">
        {chain && (
          <Card title={`${chain.project} — spend → cash`} sub={chain.ads ? `${chain.ads.from_d} → ${chain.ads.to_d}` : ''}>
            {chain.ads && (
              <div className="space-y-1.5 text-xs max-w-md">
                {[
                  { l: 'Ad spend', v: `$${(chain.ads.spend || 0).toLocaleString()}`, w: 100 },
                  { l: 'Opt-ins', v: `${chain.ads.optins || 0} · $${chain.ads.optins ? (chain.ads.spend / chain.ads.optins).toFixed(2) : '—'} each`, w: 96 },
                  { l: 'Booked', v: `${chain.ads.booked || 0} · $${cpb} each`, w: 14 },
                ].map(r => (
                  <div key={r.l} className="flex items-center gap-2">
                    <span className="w-16 text-gray-400">{r.l}</span>
                    <div className="flex-1 h-4"><div className="h-full rounded-r bg-cyan-400/70" style={{ width: `${r.w}%` }} /></div>
                    <span className="text-gray-300 whitespace-nowrap tabular-nums">{r.v}</span>
                  </div>
                ))}
                {['Calls held', 'Cash collected'].map(l => (
                  <div key={l} className="flex items-center gap-2">
                    <span className="w-16 text-gray-400">{l}</span>
                    <div className="flex-1 h-4 rounded border border-dashed border-red-400/50" />
                    <span className="text-red-300">not recorded</span>
                  </div>
                ))}
              </div>
            )}
            {!!chain.notes?.length && (
              <div className="mt-3 space-y-1">
                {chain.notes.map((n: string, i: number) => (
                  <p key={i} className="text-[11px] text-amber-300/90 flex gap-1.5"><CircleAlert className="w-3.5 h-3.5 shrink-0" />{n}</p>
                ))}
              </div>
            )}
            {chain.overview?.conversion && (
              <p className="text-[11px] text-gray-500 mt-3">
                Funnel (all-time): {chain.overview.conversion.startVisitors} visitors → {chain.overview.leads?.total} leads → {chain.overview.appointments?.booked} booked
                ({chain.overview.conversion.visitorToBookedRate}% visitor→booked)
              </p>
            )}
          </Card>
        )}

        <Card title="Closed-loop verdicts" sub="Every executed change, measured before → after from live GSC data.">
          {!(data.verdicts || []).length && <p className="text-xs text-gray-600">No executed changes yet.</p>}
          <div className="space-y-2.5">
            {(data.verdicts || []).map((v: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                {v.verdict === 'improved' ? <CircleCheck className="w-4 h-4 text-green-300 shrink-0 mt-0.5" /> : <Hourglass className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <p className="text-gray-300 truncate">{v.title} <span className="text-gray-600">· {v.executedAt}</span></p>
                  <p className="text-gray-500 tabular-nums">
                    CTR {v.before?.ctr ?? '—'}% → <span className="text-white">{v.after?.ctr ?? '—'}%</span> · clicks {v.before?.cl ?? '—'} → {v.after?.cl ?? '—'}
                    <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${VERDICT_CLS[v.verdict] || VERDICT_CLS.pending}`}>{v.verdict}</span>
                    {!v.recorded && <span className="ml-1 text-[10px] text-gray-600">(not yet in improve.db)</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Work queues */}
      <Card title="Portfolio work queue" sub={`Top of ${data.queues?.cliff ?? '—'} cliff queries (pos 11–20 by impressions) and the top-10 CTR laggards — each is one content/title rec.`}>
        <div className="grid lg:grid-cols-2 gap-5">
          <div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Position cliff</p>
            <QueryTable rows={data.queues?.cliffTop || []} />
          </div>
          <div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">CTR laggards (already top-10)</p>
            <QueryTable rows={data.queues?.laggardsTop || []} showCtr />
          </div>
        </div>
        <a href="/improve" className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 mt-3">
          Turn these into recommendations in /improve <ExternalLink className="w-3 h-3" />
        </a>
      </Card>
    </div>
  );
}
